import { absoluteValue, toStringBigDecimal } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { bybitAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  const response = await client.run({
    method: 'GET',
    path: '/v5/position/closed-pnl',
    query: {
      category: 'linear',
      limit: config.limit.toString(),
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
    },
    responseSchema,
  })

  // Sort by createdTime in descending order
  const sortedItems = response.result.list.sort(
    (a, b) => Number(b.createdTime) - Number(a.createdTime),
  )

  // データ処理
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const actions: Action[] = []

  for (const item of sortedItems) {
    const timestamp = Number(item.createdTime)

    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? timestamp : Math.min(fetchedFrom, timestamp)
    fetchedTo =
      fetchedTo === undefined ? timestamp : Math.max(fetchedTo, timestamp)

    // Determine assetSymbol based on symbol suffix
    let assetSymbol: string
    if (item.symbol.endsWith('USDT')) {
      assetSymbol = 'USDT'
    } else if (item.symbol.endsWith('PERP') || item.symbol.includes('-')) {
      assetSymbol = 'USDC'
    } else if (item.symbol.endsWith('USD')) {
      assetSymbol = item.symbol.slice(0, -3)
    } else if (item.symbol.endsWith('USDH25')) {
      assetSymbol = item.symbol.slice(0, -6)
    } else {
      throw new Error(`Unsupported symbol format: ${item.symbol}`)
    }

    const asset = getAsset(assetSymbol)
    if (!asset) {
      throw new AssetNotFoundException(assetSymbol)
    }

    // Skip if closedPnl is zero
    if (item.closedPnl.value === 0n) {
      continue
    }

    if (item.execType === 'Trade' || item.execType === 'BustTrade') {
      // Trade/BustTrade case
      const positionType = item.side === 'Buy' ? 'Short' : 'Long'
      const action: Action = {
        comment: `Close ${positionType} Position: ${toStringBigDecimal(item.closedSize)}[${item.symbol}]`,
        evidence: 'system-rule',
        order: 0,
        source: `${bybitAccountCategory}_${config.type}_${item.orderId}`,
        type: item.closedPnl.value > 0n ? 'realize-profit' : 'realize-loss',
        timestamp,
        transfers: [
          {
            asset,
            amount: absoluteValue(item.closedPnl),
            direction: item.closedPnl.value > 0n ? 'in' : 'out',
          },
        ],
      }
      actions.push(action)
    } else if (
      item.execType === 'SessionSettlePnL' ||
      item.execType === 'Settle'
    ) {
      // SessionSettlePnL/Settle case
      const positionType = item.side === 'Buy' ? 'Long' : 'Short'
      const action: Action = {
        comment: `${positionType} Position: ${toStringBigDecimal(item.qty)}[${item.symbol}]`,
        evidence: 'system-rule',
        order: 0,
        source: `${bybitAccountCategory}_${config.type}_${item.orderId}`,
        type:
          item.closedPnl.value > 0n ? 'derivative-profit' : 'derivative-fee',
        timestamp,
        transfers: [
          {
            asset,
            amount: absoluteValue(item.closedPnl),
            direction: item.closedPnl.value > 0n ? 'in' : 'out',
          },
        ],
      }
      actions.push(action)
    } else {
      throw new Error(`Unsupported execType: ${item.execType}`)
    }
  }

  // Sort actions by timestamp in descending order
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.result.list.length,
    from: fetchedFrom,
    sources: [],
    stopped: false,
    to: fetchedTo,
  }
}
